// @flow
import Hammer from 'hammerjs';

import React from 'react';
import { Link } from 'react-router-dom';
import { Translate } from 'react-redux-i18n';
import { getCurrentPcId } from '../../../utils/urlUtils';
import { branchLevelColor, isMobile, reconciliationStates } from '../../../const';

import VisioGlobeUI from '../../common/VisioGlobeUI';
import type { MapViewer } from '../../../types/VisioglobeType';
import geminiApi from '../../../network/api/geminiApi';
import shoppingCenterApi from '../../../network/api/shoppingCenterApi';
import type { MallStore, ReconciliationStore } from '../../../types/shoppingCenterType';
import visioGlobeApi from '../../../network/api/visioGlobeApi';
import Mapviewer from '../map/mapviewer';
import type { FootFallDoors } from '../../../types/geminiType';
import NoElementFound from '../../../components/NoElementFound';

type Props = {
  selectFootfallDoor: (footfallData: FootFallDoors, footfallDataList: FootFallDoors[]) => void,
  fetchedFootfallDoor: (footfallDataList: FootFallDoors[]) => void,
}

type State = {
  mapReady: boolean,
  noMap: boolean,
  footfallDataList: FootFallDoors[],
}

class FlowMap extends React.Component<Props, State> {
  state: State = {
    mapReady: false,
    noMap: false,
    footfallDataList: [],
  };

  mapviewer: MapViewer;

  ref: HTMLDivElement | null;

  scrollRef: HTMLDivElement | null;

  componentDidMount() {
    const currentPcId = getCurrentPcId();

    visioGlobeApi.fetchHashmap(currentPcId)
      .then(response => response.json())
      .then((responseJson: { hashmap: string }) => {
        this.mapviewer = new Mapviewer(
          responseJson.hashmap,
          getCurrentPcId(),
          isMobile,
          false,
          'container',
          () => {
            this.setState({ mapReady: true });
            this.mapviewer.setSelectPoiCallback(this.selectPoiCallback);

            const storePromise = shoppingCenterApi.fetchStores(getCurrentPcId())
              .then(response => response.json());

            const reconciliationPromise = geminiApi.fetchReconciliation(getCurrentPcId())
              .then(response => response.json());

            const footfallPromise = geminiApi.fetchShoppingCenterDwellPercent(getCurrentPcId())
              .then(response => response.json());

            geminiApi.fetchShoppingCenterFootfalls(getCurrentPcId())
              .then(response => response.json())
              .then((json) => {
                const list = json.map(data => data.footfallData);
                this.setState({ footfallDataList: list });
                this.props.fetchedFootfallDoor(list);
                this.mapviewer.loadPois(json);
              });

            // eslint-disable-next-line compat/compat
            Promise.all([storePromise, reconciliationPromise, footfallPromise])
              .then((values: [MallStore[], ReconciliationStore[], any]) => {
                this.initMap(values[0], values[1]);
              });
          },
        );
      })
      .then(() => {
        document.dispatchEvent(new Event('mapviewerready'));
      })
      .catch(() => this.setState({ noMap: true }));
  }

  selectPoiCallback = (footfallData: FootFallDoors) => {
    this.props.selectFootfallDoor(footfallData, this.state.footfallDataList);
  }

  initMap = (stores: MallStore[], reconciliationList: ReconciliationStore[]): void => {
    const colorMap = {};
    stores.forEach((store: MallStore) => {
      const reconciliationStore = reconciliationList
        .filter(elt => (
          elt.idMercury === store.storeId || elt.retailerName === store.retailerName
        ))[0];
      if (reconciliationStore && reconciliationStore.idVisioglobes) {
        colorMap[reconciliationStore.idVisioglobes[0]] = branchLevelColor[store.parentBranchId];
      }
    });
    this.mapviewer.loadLabelsBaseMap(
      reconciliationList.filter(str => str.state === reconciliationStates.OK
        || str.state === reconciliationStates.MAPPED),
      colorMap,
    );
  };

  render() {
    return (
      <div
        ref={(ref) => {
          this.scrollRef = ref;
        }}
        className="mall-map-container scrollable-content"
      >
        {!this.state.noMap &&
        <div className="mapviewer-container">
          <div
            id="vg_mapviewer"
          >
            <div
              ref={(ref) => {
                if (isMobile && !this.ref && ref) {
                  this.ref = ref;
                  const hammertime = new Hammer(ref);
                  hammertime.get('pinch').set({ enable: true });
                }
              }}
              id="container"
              className="visiomap"
            />
            <div id="progress">
              <input
                value="0"
                data-width="150"
                data-readonly="true"
                data-thickness=".2"
                className="knob"
                readOnly="readonly"
              />
            </div>
          </div>
          {
            this.state.mapReady && (
              <VisioGlobeUI mapviewer={this.mapviewer} />
            )
          }
        </div>
        }
        {this.state.noMap &&
          <NoElementFound text={<Translate value="mallMap.NO_MAP" />} >
            <div className="link">
              <Link href={`/mall/media?mall=${getCurrentPcId()}`} to={`/mall/media?mall=${getCurrentPcId()}`}>
                <Translate value="mallMap.REDIRECT" /> <i className="fa fa-arrow-right" />
              </Link>
            </div>
          </NoElementFound>
        }
      </div>
    );
  }
}

export default FlowMap;
